import React, { useState } from 'react';
import { CollegeRegistration, EventName, EventTeam, Participant } from '../types';

interface FormProps {
  onRegister: (data: Omit<CollegeRegistration, 'id' | 'timestamp' | 'passId' | 'hasCheckedIn' | 'hasLunchUsed' | 'isFinalized' | 'unitOf' | 'sdCount' | 'swCount'>) => void;
  onBack: () => void;
}

const EVENTS_META: Record<EventName, { min: number, max: number, wing: 'SD' | 'SW' | 'Both' }> = {
  'Quarter Guard SD': { min: 8, max: 8, wing: 'SD' },
  'Quarter Guard SW': { min: 8, max: 8, wing: 'SW' },
  'Squad Drill SD': { min: 10, max: 10, wing: 'SD' },
  'Squad Drill SW': { min: 10, max: 10, wing: 'SW' },
  'Best Cadet SD': { min: 1, max: 1, wing: 'SD' },
  'Best Cadet SW': { min: 1, max: 1, wing: 'SW' },
  'Tug of War SW': { min: 8, max: 8, wing: 'SW' },
  'Volleyball SD': { min: 8, max: 8, wing: 'SD' },
  'Extempore': { min: 1, max: 1, wing: 'Both' },
  'Quiz Competition': { min: 1, max: 1, wing: 'Both' },
  'Cultural': { min: 8, max: 8, wing: 'Both' },
  'Exhibition': { min: 4, max: 4, wing: 'Both' },
  'Painting': { min: 1, max: 1, wing: 'Both' },
  'Photography': { min: 1, max: 1, wing: 'Both' },
};


export const RegistrationForm: React.FC<FormProps> = ({ onRegister, onBack }) => {
  const [step, setStep] = useState(1);
  const [collegeData, setCollegeData] = useState({
    collegeName: '',
    anoRankName: '',
    anoContact: '',
    seniorRankName: '',
    seniorContact: '',
    totalParticipants: 0,
    battalion: '',
    nccWing: '',
    email: ''
  });

  const [selectedEvents, setSelectedEvents] = useState<EventName[]>([]);
  const [eventTeams, setEventTeams] = useState<Record<string, Participant[]>>({});

  const handleToggleEvent = (eventName: EventName) => {
    if (selectedEvents.includes(eventName)) {
      setSelectedEvents(selectedEvents.filter(e => e !== eventName));
      const newTeams = { ...eventTeams };
      delete newTeams[eventName];
      setEventTeams(newTeams);
    } else {
      setSelectedEvents([...selectedEvents, eventName]);
      const meta = EVENTS_META[eventName];
      setEventTeams(prev => ({
        ...prev,
        [eventName]: Array(meta.min).fill(null).map(() => ({ 
          rank: '', 
          name: '', 
          regimentalNumber: '', 
          mobileNumber: '',
          wing: meta.wing === 'Both' ? 'SD' : meta.wing
        }))
      }));
    }
  };

  const handleParticipantChange = (eventName: string, index: number, field: keyof Participant, value: string) => {
    setEventTeams(prev => {
      const updated = [...prev[eventName]];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, [eventName]: updated };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const teams: EventTeam[] = selectedEvents.map(eventName => ({
      eventName,
      participants: eventTeams[eventName]
    }));
    
    onRegister({
      ...collegeData,
      teams
    });
  };

  const inputClass = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 focus:ring-1 focus:ring-gold focus:outline-none transition-all placeholder:text-gray-600 text-xs";
  const labelClass = "block text-[10px] font-bold text-gold uppercase tracking-widest mb-1";

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {step === 1 && (
        <div className="glass p-8 rounded-3xl space-y-8 animate-in zoom-in-95">
          <div className="text-center">
            <h3 className="font-stencil text-2xl text-white">COLLEGE ENROLLMENT</h3>
            <p className="text-gray-500 text-sm uppercase tracking-widest font-bold mt-2">Nominal Roll Initiation</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className={labelClass}>NAME OF COLLEGE</label>
              <input 
                required
                className={inputClass} 
                value={collegeData.collegeName}
                onChange={e => setCollegeData({...collegeData, collegeName: e.target.value})}
                placeholder="Enter Full College Name"
              />
            </div>
            
            <div>
              <label className={labelClass}>RANK & NAME OF ANO</label>
              <input required className={inputClass} value={collegeData.anoRankName} onChange={e => setCollegeData({...collegeData, anoRankName: e.target.value})} placeholder="e.g. Lt. John Doe" />
            </div>
            <div>
              <label className={labelClass}>ANO CONTACT NO</label>
              <input required className={inputClass} value={collegeData.anoContact} onChange={e => setCollegeData({...collegeData, anoContact: e.target.value})} placeholder="e.g. 9876543210" />
            </div>

            <div>
              <label className={labelClass}>RANK & NAME OF COLLEGE SENIOR</label>
              <input required className={inputClass} value={collegeData.seniorRankName} onChange={e => setCollegeData({...collegeData, seniorRankName: e.target.value})} placeholder="e.g. SUO Jane Smith" />
            </div>
            <div>
              <label className={labelClass}>SENIOR CONTACT NO</label>
              <input required className={inputClass} value={collegeData.seniorContact} onChange={e => setCollegeData({...collegeData, seniorContact: e.target.value})} placeholder="e.g. 8765432109" />
            </div>

            <div>
              <label className={labelClass}>TOTAL DELEGATION STRENGTH</label>
              <input type="number" required className={inputClass} value={collegeData.totalParticipants || ''} onChange={e => setCollegeData({...collegeData, totalParticipants: parseInt(e.target.value) || 0})} placeholder="Total Cadets arriving" />
            </div>
            <div>
              <label className={labelClass}>BATTALION</label>
              <input required className={inputClass} value={collegeData.battalion} onChange={e => setCollegeData({...collegeData, battalion: e.target.value})} placeholder="e.g. 7 DELHI BATTALION" />
            </div>
            <div>
              <label className={labelClass}>NCC WING</label>
              <select required className={`${inputClass} appearance-none`} value={collegeData.nccWing} onChange={e => setCollegeData({...collegeData, nccWing: e.target.value})}>
                <option value="">Select Wing</option>
                <option value="Army">Army Wing</option>
                <option value="Navy">Navy Wing</option>
                <option value="Air Force">Air Force Wing</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>OFFICIAL EMAIL</label>
              <input type="email" required className={inputClass} value={collegeData.email} onChange={e => setCollegeData({...collegeData, email: e.target.value})} placeholder="official@college.edu" />
            </div>
          </div>
          <button onClick={() => setStep(2)} className="w-full bg-gold text-black py-4 rounded-xl font-black text-lg hover:scale-[1.01] active:scale-95 transition-all shadow-xl shadow-gold/10 uppercase tracking-widest">
            Enter Team Details &rarr;
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="animate-in fade-in slide-in-from-right-4">
          <div className="flex items-center justify-between mb-8">
            <button onClick={() => setStep(1)} className="text-gray-500 hover:text-white uppercase text-[10px] font-bold tracking-widest">&larr; Back to Info</button>
            <h3 className="font-stencil text-2xl text-gold">NOMINAL ROLL ENTRY</h3>
            <div className="w-20"></div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {(Object.keys(EVENTS_META) as EventName[]).map(event => (
              <button
                key={event}
                onClick={() => handleToggleEvent(event)}
                className={`p-4 rounded-xl border text-[10px] font-black tracking-widest transition-all ${
                  selectedEvents.includes(event)
                    ? 'bg-red-900 border-gold text-white shadow-lg'
                    : 'bg-white/5 border-white/10 text-gray-500 hover:border-white/30'
                }`}
              >
                {event.toUpperCase()}
              </button>
            ))}
          </div>

          {selectedEvents.length > 0 ? (
            <form onSubmit={handleSubmit} className="space-y-16">
              {selectedEvents.map(event => (
                <div key={event} className="space-y-6">
                  <div className="flex justify-between items-center border-b border-gold/20 pb-4">
                    <h4 className="font-stencil text-xl text-white uppercase tracking-widest">{event} Team</h4>
                    <span className="text-[9px] bg-white/5 px-3 py-1 rounded border border-white/10 text-gold uppercase font-bold tracking-widest">
                      {EVENTS_META[event].min} Cadets Required
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {eventTeams[event].map((p, idx) => (
                      <div key={idx} className="glass p-5 rounded-2xl border border-white/5 space-y-4 relative overflow-hidden">
                        <div className="flex justify-between items-center">
                          <p className="text-[10px] font-black text-gold uppercase tracking-widest">Cadet {idx + 1}</p>
                           {EVENTS_META[event].wing === 'Both' && (
                            <select name="wing" value={p.wing} onChange={(e) => handleParticipantChange(event, idx, 'wing', e.target.value)} className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs">
                              <option value="SD">SD</option>
                              <option value="SW">SW</option>
                            </select>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-1">
                            <label className="text-[8px] text-gray-500 uppercase font-black mb-1 block">Rank</label>
                            <input required placeholder="Cdt / SUO..." className={inputClass} value={p.rank} onChange={(e) => handleParticipantChange(event, idx, 'rank', e.target.value)} />
                          </div>
                          <div className="col-span-1">
                            <label className="text-[8px] text-gray-500 uppercase font-black mb-1 block">Full Name</label>
                            <input required placeholder="As per ID" className={inputClass} value={p.name} onChange={(e) => handleParticipantChange(event, idx, 'name', e.target.value)} />
                          </div>
                          <div className="col-span-1">
                            <label className="text-[8px] text-gray-500 uppercase font-black mb-1 block">Regimental No.</label>
                            <input required placeholder="DL22SD..." className={inputClass} value={p.regimentalNumber} onChange={(e) => handleParticipantChange(event, idx, 'regimentalNumber', e.target.value)} />
                          </div>
                          <div className="col-span-1">
                            <label className="text-[8px] text-gray-500 uppercase font-black mb-1 block">Mobile No.</label>
                            <input required placeholder="10 Digits" className={inputClass} value={p.mobileNumber} onChange={(e) => handleParticipantChange(event, idx, 'mobileNumber', e.target.value)} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="pt-8">
                <button type="submit" className="w-full bg-red-900 text-white py-6 rounded-2xl font-black text-2xl shadow-2xl shadow-red-900/40 hover:bg-red-800 transition-all transform hover:-translate-y-1 uppercase tracking-widest">
                  Confirm Registration
                </button>
              </div>
            </form>
          ) : (
             <div className="glass p-20 text-center rounded-3xl border-dashed border-2 border-white/10">
               <p className="text-gray-600 uppercase font-black tracking-[0.3em]">Select at least one event above</p>
             </div>
          )}
        </div>
      )}
    </div>
  );
};
